// ============================================
// FILE: src/components/FashionProductScreen.jsx
// ============================================

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, ShoppingBag, Heart, Tag, Truck, RotateCcw, BadgeCheck } from 'lucide-react';
import FashionProductGallery from './fashion/FashionProductGallery';
import ColorSwatchSelector from './fashion/ColorSwatchSelector';
import SizeSelector from './fashion/SizeSelector';
import SizeGuideSheet from './fashion/SizeGuideSheet';
import FashionSpecsAccordion from './fashion/FashionSpecsAccordion';
import ProductRail from './fashion/ProductRail';
import ProductReviews from './fashion/ProductReviews';
import QuickCheckoutButton from './fashion/QuickCheckoutButton';
import { addRecentlyViewed, getRecentlyViewedExcluding } from '../utils/recentlyViewed';
import { normalizeMedia, getMediaThumb } from '../utils/mediaHelpers';
import {
  getOptions,
  findVariant,
  isValueAvailable,
  getDefaultSelection,
  isSelectionComplete,
  buildCartPayload,
  getSelectionPrice,
} from '../utils/variantHelpers';
import { getRegularPrice, hasDiscount, getDiscountPercent } from '../utils/priceHelpers';
import apiService from '../apiService';

const ASSURANCES = [
  { Icon: RotateCcw, label: '15 days return' },
  { Icon: Truck, label: 'Free delivery' },
  { Icon: BadgeCheck, label: '100% original' },
];

const FashionProductScreen = ({
  productId = null,
  product: initialProduct = null,
  isOpen = true,
  onClose = () => {},
  addToCart,
  cart = [],
  getCartCount,
  getCartTotal,
  openCheckout,
  toggleFavorite,
  favorites = [],
  showToast,
  appTheme = 'white',
  isDesktop = false,
  headerSlot = null,
}) => {
  const [product, setProduct] = useState(initialProduct);
  const [loading, setLoading] = useState(!initialProduct);
  const [selection, setSelection] = useState({});
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [sizeHighlight, setSizeHighlight] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [recent, setRecent] = useState([]);
  const [railsLoading, setRailsLoading] = useState(false);

  const sizeRef = useRef(null);
  const scrollRef = useRef(null);
  const propKey = productId ?? initialProduct?.slug ?? initialProduct?.id;

  // Tapping a card in one of the rails swaps the product in place rather than
  // stacking another screen, so back still returns to the grid.
  const [lookupKey, setLookupKey] = useState(propKey);
  useEffect(() => { setLookupKey(propKey); }, [propKey]);

  const openRelated = (next) => {
    if (!next?.id) return;
    setProduct(next);                 // paint the card data immediately
    setSelection({});
    setLookupKey(next.slug || next.id);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (lookupKey == null) return;
      setLoading(!initialProduct);

      const resp = await apiService.getProductDetail(lookupKey);
      if (cancelled) return;

      const full = resp?.data?.product;
      if (full) {
        setProduct((current) => ({ ...(current || {}), ...full }));
        setSelection(getDefaultSelection(full));
      }
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && product?.id != null) {
      apiService.trackPageView(`product:${product.id}`, {
        title: product.name,
        kind: 'product',
        product_id: product.id,
      });
    }
  }, [isOpen, product?.id, product?.name]);

  // Similar + recently viewed.
  //
  // The recently-viewed ids are read BEFORE recording this product, so the
  // rail shows what came before rather than leading with the current page.
  useEffect(() => {
    const currentId = product?.id;
    if (!isOpen || currentId == null) return undefined;

    let cancelled = false;

    const loadRails = async () => {
      setRailsLoading(true);

      const previousIds = getRecentlyViewedExcluding(currentId, 12);
      addRecentlyViewed(currentId);

      const [similarRes, recentRes] = await Promise.all([
        apiService.getSimilarProducts(currentId, 12),
        previousIds.length > 0
          ? apiService.getProductsByIds(previousIds)
          : Promise.resolve({ data: { products: [] } }),
      ]);

      if (cancelled) return;

      setSimilar(similarRes?.data?.products || []);
      setRecent(recentRes?.data?.products || []);
      setRailsLoading(false);
    };

    loadRails();
    return () => { cancelled = true; };
  }, [isOpen, product?.id]);

  const options = useMemo(() => getOptions(product), [product]);
  const media = useMemo(() => normalizeMedia(product), [product]);

  const colorOption = options.find((o) => o.code === 'color' || o.type === 'swatch');
  const sizeOption = options.find((o) => o.code === 'size');
  const otherOptions = options.filter((o) => o !== colorOption && o !== sizeOption);

  const activeColorValueId = colorOption
    ? colorOption.values.find((v) => String(v.value) === String(selection[colorOption.code]))?.id ?? null
    : null;

  const entry = findVariant(product, selection);
  const finalPrice = getSelectionPrice(product, entry);

  // First photo of each colourway, so the colour picker shows the garment.
  const colourThumbnail = useMemo(() => (value) => {
    const row = media.find((m) => String(m.attribute_value_id) === String(value.id) && m.media_type !== 'video');
    return row ? getMediaThumb(row) : null;
  }, [media]);

  if (!isOpen || !product) return null;

  const favArray = Array.isArray(favorites) ? favorites : [];
  const isFav = favArray.some((item) => String(item?.id) === String(product.id));

  const sizeChart = product.size_chart || null;
  const discounted = hasDiscount(product);
  const outOfStock = Boolean(entry) && !entry.is_in_stock;
  const needsSelection = options.length > 0 && !isSelectionComplete(product, selection);

  const breadcrumb = typeof product.category === 'object'
    ? product.category?.path
    : product.category;

  const handleOptionChange = (code, value) => {
    setSelection((current) => {
      const next = { ...current, [code]: value };

      // Changing colour can strand the chosen size — an Olive XL that does not
      // exist. Clear it rather than leave an impossible combination selected.
      Object.keys(next).forEach((otherCode) => {
        if (otherCode === code) return;
        if (!isValueAvailable(product, otherCode, next[otherCode], { [code]: value })) {
          delete next[otherCode];
        }
      });

      return next;
    });
    setSizeHighlight(false);
  };

  const handleAddToBag = () => {
    if (needsSelection) {
      setSizeHighlight(true);
      sizeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setSizeHighlight(false), 2600);
      return;
    }

    if (outOfStock) {
      showToast?.('This size is out of stock', 'error');
      return;
    }

    addToCart?.(buildCartPayload(product, entry), 1);

    const label = entry ? `${product.name} (${entry.name})` : product.name;
    showToast?.(`${label} added to bag`, 'success');
  };

  const buyBox = (
    <div className={isDesktop ? 'flex-1 min-w-0 pl-2' : 'px-4'}>
      <h1 style={{ fontSize: isDesktop ? 24 : 20, fontWeight: 700, color: 'var(--fx-ink)', lineHeight: 1.2 }}>
        {product.brand || product.name}
      </h1>
      {product.brand && (
        <p style={{ fontSize: isDesktop ? 20 : 16, color: 'var(--fx-ink-2)', marginTop: 4, lineHeight: 1.3 }}>
          {product.name}
        </p>
      )}

      {Number(product.rating) > 0 && (
        <div
          className="inline-flex items-center gap-1.5 mt-3 px-2 py-1"
          style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 14, fontWeight: 700 }}
        >
          <span>{Number(product.rating).toFixed(1)}</span>
          <span style={{ color: 'var(--fx-green)' }}>★</span>
          {product.review_count > 0 && (
            <>
              <span style={{ color: 'var(--fx-line)' }}>|</span>
              <span style={{ color: 'var(--fx-ink-3)', fontWeight: 400 }}>
                {product.review_count} Ratings
              </span>
            </>
          )}
        </div>
      )}

      <div className="mt-5 pt-5" style={{ borderTop: '1px solid var(--fx-line)' }}>
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--fx-ink)' }}>
            ₹{finalPrice}
          </span>
          {discounted && (
            <>
              <span style={{ fontSize: 20, color: 'var(--fx-ink-2)' }}>
                MRP <span style={{ textDecoration: 'line-through' }}>₹{getRegularPrice(product)}</span>
              </span>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--fx-off)' }}>
                ({getDiscountPercent(product)}% OFF)
              </span>
            </>
          )}
        </div>
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--fx-green)', marginTop: 4 }}>
          inclusive of all taxes
        </p>
      </div>

      <div className="mt-7">
        {colorOption && (
          <ColorSwatchSelector
            option={colorOption}
            selectedValue={selection[colorOption.code]}
            onChange={(value) => handleOptionChange(colorOption.code, value)}
            isAvailable={(value) => isValueAvailable(product, colorOption.code, value, selection)}
            thumbnailFor={colourThumbnail}
          />
        )}

        {sizeOption && (
          <div ref={sizeRef}>
            <SizeSelector
              option={sizeOption}
              selectedValue={selection[sizeOption.code]}
              onChange={(value) => handleOptionChange(sizeOption.code, value)}
              isAvailable={(value) => isValueAvailable(product, sizeOption.code, value, selection)}
              onOpenSizeGuide={sizeChart ? () => setShowSizeGuide(true) : null}
              highlight={sizeHighlight}
            />
          </div>
        )}

        {otherOptions.map((option) => (
          <SizeSelector
            key={option.code}
            option={option}
            selectedValue={selection[option.code]}
            onChange={(value) => handleOptionChange(option.code, value)}
            isAvailable={(value) => isValueAvailable(product, option.code, value, selection)}
          />
        ))}
      </div>

      {/* Desktop keeps the buttons inline; mobile pins them to the bottom. */}
      {isDesktop && (
        <div className="flex gap-4 mt-2">
          <button
            type="button"
            onClick={handleAddToBag}
            disabled={outOfStock}
            className="fx-btn fx-btn-primary"
            style={{ flex: '1 1 0', maxWidth: 280 }}
          >
            <ShoppingBag size={18} />
            {outOfStock ? 'Out of Stock' : 'Add to Bag'}
          </button>

          {typeof toggleFavorite === 'function' && (
            <button
              type="button"
              onClick={() => toggleFavorite(product)}
              className="fx-btn fx-btn-ghost"
              style={{ flex: '1 1 0', maxWidth: 200 }}
            >
              <Heart size={18} fill={isFav ? 'currentColor' : 'none'} className={isFav ? 'text-[#ff3f6c]' : ''} />
              {isFav ? 'Wishlisted' : 'Wishlist'}
            </button>
          )}
        </div>
      )}

      <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--fx-line)' }}>
        <h3 className="fx-label mb-3">Delivery Options</h3>
        <div className="space-y-2.5">
          {ASSURANCES.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5">
              <Icon size={16} style={{ color: 'var(--fx-ink-3)' }} />
              <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {discounted && (
        <div
          className="mt-6 flex items-start gap-2.5 p-3"
          style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}
        >
          <Tag size={16} style={{ color: 'var(--fx-pink)', marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 14, fontWeight: 700 }}>Best Price</p>
            <p style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
              You save ₹{(getRegularPrice(product) - finalPrice).toFixed(0)} on this order
            </p>
          </div>
        </div>
      )}

      <FashionSpecsAccordion
        specs={product.specs}
        description={product.description}
        product={product}
      />
    </div>
  );

  return (
    <div
      ref={scrollRef}
      className={`fashion-scope fixed inset-0 z-[300] bg-white page-overlay-scroll ${isDesktop ? 'desktop-scrollbar' : ''}`}
    >
      {headerSlot}

      <div className={`w-full mx-auto ${isDesktop ? 'max-w-[1200px] px-6' : ''} ${isDesktop ? 'pb-16' : 'pb-24'}`}>
        {/* Mobile back bar; desktop relies on the navbar plus the breadcrumb */}
        {!isDesktop && (
          <div
            className="sticky top-0 z-10 bg-white px-3 py-3 flex items-center gap-3"
            style={{ borderBottom: '1px solid var(--fx-line)' }}
          >
            <button
              type="button"
              onClick={onClose}
              className="p-1 -ml-1"
              aria-label="Go back"
            >
              <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
            </button>
            <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>
              {product.brand || product.name}
            </span>
          </div>
        )}

        {isDesktop && (
          <div className="flex items-center gap-2 py-4" style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>
            <button type="button" onClick={onClose} style={{ fontWeight: 700, color: 'var(--fx-ink-2)' }}>
              Back
            </button>
            {breadcrumb && (
              <>
                <span>/</span>
                <span>{breadcrumb}</span>
              </>
            )}
          </div>
        )}

        {loading && !product.name ? (
          <div className={isDesktop ? 'flex gap-8 pt-2' : 'pt-2'}>
            <div className={isDesktop ? 'w-[600px]' : 'w-full'}>
              <div className="w-full bg-[#f5f5f6] animate-pulse" style={{ aspectRatio: '3 / 4' }} />
            </div>
            <div className="flex-1 px-4 pt-4 space-y-3">
              <div className="h-6 bg-[#f5f5f6] w-2/3 animate-pulse" />
              <div className="h-5 bg-[#f5f5f6] w-1/2 animate-pulse" />
              <div className="h-8 bg-[#f5f5f6] w-1/3 animate-pulse" />
            </div>
          </div>
        ) : (
          <div className={isDesktop ? 'flex gap-8 items-start' : ''}>
            <div className={isDesktop ? 'w-[600px] flex-shrink-0' : 'mb-5'}>
              <FashionProductGallery
                media={media}
                activeValueId={activeColorValueId}
                productName={product.name}
                isDesktop={isDesktop}
              />
            </div>
            {buyBox}
          </div>
        )}

        {/* Reviews and rails sit below the two-column layout so they run the
            full width of the page rather than being trapped in the buy-box
            column. */}
        {!loading && (
          <div className={isDesktop ? '' : 'px-4'}>
            <ProductReviews
              product={product}
              isAuthenticated={apiService.isAuthenticated()}
              showToast={showToast}
              isDesktop={isDesktop}
              // Keep the header chip in step when a review lands, so the count
              // above does not contradict the list below.
              onSummaryChange={(s) => setProduct((p) => (
                p ? { ...p, rating: s.average, review_count: s.total } : p
              ))}
            />

            <ProductRail
              title="Similar Products"
              products={similar}
              loading={railsLoading && similar.length === 0}
              onOpen={openRelated}
              toggleFavorite={toggleFavorite}
              favorites={favorites}
              isDesktop={isDesktop}
            />

            <ProductRail
              title="Recently Viewed"
              products={recent}
              onOpen={openRelated}
              toggleFavorite={toggleFavorite}
              favorites={favorites}
              isDesktop={isDesktop}
            />
          </div>
        )}
      </div>

      {/* Mobile sticky action bar */}
      {!isDesktop && !loading && (
        <div
          className="fixed inset-x-0 bottom-0 bg-white flex gap-3 px-3 py-2.5"
          style={{ borderTop: '1px solid var(--fx-line)', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          {typeof toggleFavorite === 'function' && (
            <button
              type="button"
              onClick={() => toggleFavorite(product)}
              className="fx-btn fx-btn-ghost"
              style={{ flex: '1 1 0' }}
            >
              <Heart size={17} fill={isFav ? 'currentColor' : 'none'} className={isFav ? 'text-[#ff3f6c]' : ''} />
              {isFav ? 'Wishlisted' : 'Wishlist'}
            </button>
          )}
          <button
            type="button"
            onClick={handleAddToBag}
            disabled={outOfStock}
            className="fx-btn fx-btn-primary"
            style={{ flex: '1.3 1 0' }}
          >
            <ShoppingBag size={17} />
            {outOfStock ? 'Out of Stock' : 'Add to Bag'}
          </button>
        </div>
      )}


      <QuickCheckoutButton
        cart={cart}
        getCartCount={getCartCount}
        getCartTotal={getCartTotal}
        openCheckout={openCheckout}
        bottomOffset={isDesktop ? 'bottom-6' : 'bottom-24'}
      />

      <SizeGuideSheet
        chart={sizeChart}
        isOpen={showSizeGuide}
        onClose={() => setShowSizeGuide(false)}
        isDesktop={isDesktop}
      />
    </div>
  );
};

export default FashionProductScreen;
